import { useTranslation } from 'react-i18next';

type DeliveryStatus = 'heading_to_pickup' | 'at_pickup' | 'picked_up' | 'heading_to_customer' | 'at_customer';

interface DeliveryStepsProps {
  status: DeliveryStatus;
  pickupAddress: string;
  deliveryAddress: string;
  customerName: string;
}

export default function DeliverySteps({ status, pickupAddress, deliveryAddress, customerName }: DeliveryStepsProps) {
  const { t } = useTranslation();
  
  const steps = [
    {
      id: 'pickup',
      icon: 'ri-store-3-line',
      title: t('pickupLocation') || 'Pickup Location',
      address: pickupAddress,
      statuses: ['heading_to_pickup', 'at_pickup'],
    },
    {
      id: 'delivery',
      icon: 'ri-map-pin-user-line',
      title: t('deliveryLocation') || 'Delivery Location',
      subtitle: customerName,
      address: deliveryAddress,
      statuses: ['picked_up', 'heading_to_customer', 'at_customer'],
    },
  ];
  
  const isStepActive = (stepStatuses: string[]) => {
    return stepStatuses.includes(status);
  };
  
  const isStepCompleted = (stepId: string) => {
    if (stepId === 'pickup') {
      return ['picked_up', 'heading_to_customer', 'at_customer'].includes(status);
    }
    return false;
  };
  
  return (
    <div className="bg-white rounded-2xl p-4 shadow-md">
      <h3 className="text-sm font-bold text-gray-900 mb-4">{t('deliveryProgress') || 'Delivery Progress'}</h3>
      
      <div className="space-y-4">
        {steps.map((step, index) => {
          const active = isStepActive(step.statuses);
          const completed = isStepCompleted(step.id);
          
          return (
            <div key={step.id} className="relative">
              {index < steps.length - 1 && (
                <div className={`absolute left-5 top-12 w-0.5 h-8 ${completed ? 'bg-green-500' : 'bg-gray-200'}`}></div>
              )}
              
              <div className="flex gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                  completed ? 'bg-green-500' : active ? 'bg-[#E31837]' : 'bg-gray-200'
                }`}>
                  {completed ? (
                    <i className="ri-check-line text-white text-lg"></i>
                  ) : (
                    <i className={`${step.icon} ${active ? 'text-white' : 'text-gray-500'} text-lg`}></i>
                  )}
                </div>
                
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <p className={`text-sm font-bold ${active ? 'text-gray-900' : 'text-gray-500'}`}>
                      {step.title}
                    </p>
                    {active && (
                      <span className="px-2 py-0.5 bg-[#E31837] text-white text-xs font-bold rounded-full">
                        {t('active') || 'ACTIVE'}
                      </span>
                    )}
                    {completed && (
                      <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-bold rounded-full">
                        {t('completed') || 'DONE'}
                      </span>
                    )}
                  </div>
                  
                  {step.subtitle && (
                    <p className="text-xs font-semibold text-gray-700 mb-1">{step.subtitle}</p>
                  )}
                  
                  <p className="text-xs text-gray-600">{step.address}</p>
                  
                  {active && step.id === 'pickup' && status === 'at_pickup' && (
                    <div className="mt-2 p-2 bg-blue-50 rounded-lg flex items-start gap-2">
                      <i className="ri-information-line text-[#006491] text-sm mt-0.5"></i>
                      <p className="text-xs text-[#006491]">
                        {t('confirmPickupMessage') || 'Verify order items before confirming pickup'}
                      </p>
                    </div>
                  )}
                  
                  {active && step.id === 'delivery' && status === 'at_customer' && (
                    <div className="mt-2 p-2 bg-blue-50 rounded-lg flex items-start gap-2">
                      <i className="ri-information-line text-[#006491] text-sm mt-0.5"></i>
                      <p className="text-xs text-[#006491]">
                        {t('completeDeliveryMessage') || 'Hand over the order to the customer'}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
